import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { Copy, Download, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ErrorMessage } from '@/components/ui/error-message';
import { useAuthStore } from '@/store/authStore';
import Routes from '@/router/routes';
import TelletLogo from '@/assets/logo/tellet-logo-main.svg';
import { sixDigitCodeSchema } from '@/lib/validationSchemas';

const validationSchema = Yup.object().shape({
  code: sixDigitCodeSchema,
});

type FormData = Yup.InferType<typeof validationSchema>;

const defaultValues: FormData = {
  code: '',
};

export default function TwoFactorSetup() {
  const navigate = useNavigate();
  const {
    setup2FA,
    enable2FA,
    isLoading,
    error,
    clearError,
    requires2FASetup,
    backupCodes,
    clearBackupCodes,
  } = useAuthStore();

  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [qrError, setQrError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [step, setStep] = useState<'qr' | 'backup'>('qr');
  const setupInitiated = useRef(false);

  const {
    setValue,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  const code = watch('code');

  useEffect(() => {
    // Only redirect if we're not in the middle of showing backup codes
    if (!requires2FASetup && !backupCodes) {
      navigate(Routes.login.path);
      return;
    }

    const fetchQRCode = async () => {
      setQrError(null);
      try {
        const result = await setup2FA();
        setQrCodeUrl(result.qrCodeUrl);
        setSecret(result.secret);
      } catch (err: any) {
        setQrError(
          err?.response?.data?.message ||
            'Failed to load QR code. Please refresh the page and try again.',
        );
      }
    };

    // Guard against re-invoking setup2FA on every mount — the TOTP secret
    // stored in Redis would be overwritten, invalidating a previously scanned QR code.
    if (!setupInitiated.current && !qrCodeUrl) {
      setupInitiated.current = true;
      fetchQRCode();
    }
  }, [requires2FASetup, setup2FA, navigate, backupCodes, qrCodeUrl]);

  useEffect(() => {
    if (backupCodes && backupCodes.length > 0) {
      setStep('backup');
    }
  }, [backupCodes]);

  const handleVerify = async (data: FormData) => {
    clearError();

    if (!secret) return;

    try {
      await enable2FA(secret, data.code);
    } catch (err) {
      // Error handled in store
    }
  };

  const handleCopyBackupCodes = () => {
    if (!backupCodes) return;
    navigator.clipboard.writeText(backupCodes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadBackupCodes = () => {
    if (!backupCodes) return;
    const content = `Tellet Super Admin Backup Codes\n${'='.repeat(35)}\n\nStore these codes safely. Each code can only be used once.\n\n${backupCodes.join('\n')}\n\nGenerated: ${new Date().toISOString()}`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tellet-superadmin-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleContinue = () => {
    clearBackupCodes();
    navigate(Routes.super_admin_dashboard.path);
  };

  if (step === 'backup' && backupCodes) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <img src={TelletLogo} alt="Tellet" className="mx-auto h-12 w-auto dark:invert" />
            <h2 className="mt-6 text-2xl font-bold text-foreground">Save Your Backup Codes</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Store these codes safely. Each code can only be used once if you lose access to your
              authenticator app.
            </p>
          </div>

          <div className="bg-card rounded-lg shadow-sm border border-border p-4">
            <div className="grid grid-cols-2 gap-2 font-mono text-sm">
              {backupCodes.map((backupCode, index) => (
                <div key={index} className="bg-muted p-2 rounded text-center text-foreground">
                  {backupCode}
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={handleDownloadBackupCodes}>
              <Download size={18} className="mr-2" />
              Download
            </Button>
            <Button variant="outline" className="flex-1" onClick={handleCopyBackupCodes}>
              {copied ? (
                <>
                  <Check size={18} className="mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy size={18} className="mr-2" />
                  Copy All
                </>
              )}
            </Button>
          </div>

          <Button className="w-full" onClick={handleContinue}>
            I've Saved My Codes
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img src={TelletLogo} alt="Tellet" className="mx-auto h-12 w-auto dark:invert" />
          <h2 className="mt-6 text-2xl font-bold text-foreground">
            Set Up Two-Factor Authentication
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
          </p>
        </div>

        <div className="bg-card rounded-lg shadow-sm border border-border p-6">
          {qrError ? (
            <div className="flex flex-col items-center gap-3">
              <div className="w-48 h-48 flex items-center justify-center bg-red-50 dark:bg-red-900/30 rounded border border-red-200 dark:border-red-800">
                <p className="text-sm text-red-600 dark:text-red-400 text-center px-4">{qrError}</p>
              </div>
            </div>
          ) : qrCodeUrl ? (
            <div className="flex justify-center">
              <img src={qrCodeUrl} alt="QR Code" className="w-48 h-48" />
            </div>
          ) : (
            <div className="flex justify-center">
              <div className="w-48 h-48 bg-muted animate-pulse rounded" />
            </div>
          )}
        </div>

        <form className="space-y-4" onSubmit={handleSubmit(handleVerify)}>
          <div>
            <label htmlFor="code" className="block text-sm font-medium text-foreground mb-1">
              Enter the 6-digit code from your app
            </label>
            <Input
              id="code"
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(e) =>
                setValue('code', e.target.value.replace(/\D/g, ''), { shouldValidate: false })
              }
              className="text-center text-2xl tracking-widest"
              hasError={!!errors.code || !!error}
            />
            <ErrorMessage message={errors.code?.message} />
          </div>

          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-4">
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          <Button type="submit" className="w-full" disabled={isLoading || code.length !== 6}>
            {isLoading ? 'Verifying...' : 'Verify & Enable'}
          </Button>
        </form>
      </div>
    </div>
  );
}
